import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';

import { AuthService } from './auth.service';

const formatDuration = (milliseconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

@Component({
  selector: 'app-session-status-banner',
  imports: [PorscheDesignSystemModule],
  template: `
    @if (showBanner()) {
      <div
        class="session-status"
        [class.session-status--urgent]="urgent()"
        [class.session-status--expired]="expired()"
      >
        <div class="session-status__header">
          <p-text size="small" class="session-status__label">
            @if (expired()) {
              <strong>Session has expired</strong>
            } @else if (urgent()) {
              <strong>Session expires soon</strong>
            } @else {
              Automatic refresh not available
            }
          </p-text>
          <p-button
            type="button"
            variant="tertiary"
            icon="close"
            [compact]="true"
            [hideLabel]="true"
            aria-label="Dismiss notification"
            [disabled]="expired()"
            (click)="dismiss()"
          >
            Dismiss
          </p-button>
        </div>
        <p-text size="small" class="session-status__time">
          @if (expired()) {
            Session expired
          } @else {
            Time remaining: {{ remainingLifetime() }}
          }
        </p-text>
        <div class="session-status__bar" aria-hidden="true">
          <span class="session-status__bar-fill" [style.width.%]="progress()"></span>
        </div>
        <p-button type="button" size="small" icon="refresh" (click)="requestReauth()">
          Renew session
        </p-button>
      </div>
    }

    <p-modal
      class="session-status__confirm"
      [open]="confirmOpen()"
      [aria]="{ 'aria-label': 'Reload page' }"
      (dismiss)="cancelReauth()"
    >
      <p-heading slot="header" size="small" tag="h2">Reload page?</p-heading>
      <p-text>The page will be reloaded. Unsaved changes will be lost.</p-text>
      <div slot="footer" class="session-status__confirm-actions">
        <p-button type="button" variant="secondary" (click)="cancelReauth()">Cancel</p-button>
        <p-button type="button" (click)="reauthorize()">Reload</p-button>
      </div>
    </p-modal>
  `,
  styles: `
    :host {
      position: fixed;
      left: 1rem;
      bottom: 1rem;
      z-index: 1000;
      display: block;
    }

    .session-status {
      display: grid;
      gap: 0.5rem;
      max-width: min(22rem, calc(100vw - 2rem));
      padding: 0.875rem 1rem;
      border-radius: 0.75rem;
      background: #ffffff;
      box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.12);
      border: 1px solid rgba(0, 0, 0, 0.08);
      opacity: 1;
    }

    .session-status__header {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
    }

    .session-status--urgent {
      border-color: #b45309;
      background: color-mix(in srgb, #f59e0b 12%, white);
    }

    .session-status--expired {
      border-color: #b91c1c;
      background: color-mix(in srgb, #dc2626 16%, white);
    }

    .session-status__label {
      display: block;
      min-width: 0;
      margin: 0;
      line-height: 1.3;
      word-break: break-word;
      overflow-wrap: anywhere;
    }

    .session-status__time {
      margin: 0;
    }

    .session-status__header p-button {
      justify-self: end;
    }

    .session-status__bar {
      height: 0.35rem;
      border-radius: 999px;
      overflow: hidden;
      background: color-mix(in srgb, CanvasText 12%, transparent);
    }

    .session-status__bar-fill {
      display: block;
      height: 100%;
      border-radius: inherit;
      background: #2563eb;
    }

    .session-status__confirm {
      --p-modal-width: min(28rem, 90vw);
    }

    .session-status__confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionStatusBanner implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly now = signal(Date.now());
  private readonly dismissed = signal(false);
  protected readonly confirmOpen = signal(false);
  private readonly timer = window.setInterval(() => {
    this.now.set(Date.now());
  }, 1000);

  protected readonly remainingLifetime = computed(() => {
    const expiry = this.authService.accessTokenExpiry();
    if (expiry === null) {
      return null;
    }

    const remaining = Math.max(0, expiry - this.now());
    return formatDuration(remaining);
  });

  protected readonly urgent = computed(() => {
    const expiry = this.authService.accessTokenExpiry();
    const total = this.authService.getAccessTokenLifetime();
    if (expiry === null || total === null) {
      return false;
    }

    const remaining = Math.max(0, expiry - this.now());
    return remaining <= total * 0.2;
  });

  protected readonly expired = computed(() => {
    const expiry = this.authService.accessTokenExpiry();
    if (expiry === null) {
      return false;
    }

    return expiry <= this.now();
  });

  protected readonly showBanner = computed(() => {
    return (
      !this.dismissed() &&
      this.authService.isSilentRefreshUnavailable() &&
      this.remainingLifetime() !== null
    );
  });

  protected readonly progress = computed(() => {
    const expiry = this.authService.accessTokenExpiry();
    const total = this.authService.getAccessTokenLifetime();
    if (expiry === null || total === null || total === 0) {
      return 0;
    }

    const remaining = Math.max(0, expiry - this.now());
    return Math.max(0, Math.min(100, Math.round((remaining / total) * 100)));
  });

  ngOnDestroy(): void {
    window.clearInterval(this.timer);
  }

  protected dismiss(): void {
    this.dismissed.set(true);
  }

  protected requestReauth(): void {
    this.confirmOpen.set(true);
  }

  protected cancelReauth(): void {
    this.confirmOpen.set(false);
  }

  protected reauthorize(): void {
    this.confirmOpen.set(false);
    this.authService.reauthenticateWithIdp();
  }
}
